import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const StoreCheckout = () => {
  const navigate = useNavigate();
  const { websiteId } = useParams();
  const [items, setItems] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState('');
  const [form, setForm] = useState({
    email: '',
    name: '',
    discount_code: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const paymentsApiBase = () => {
    const base = String(API_URL || '').replace(/\/$/, '');
    if (!base) return '/api';
    if (base.endsWith('/api')) return base;
    return `${base}/api`;
  };

  const cartStorageKey = () => `cart_${websiteId}`;

  const loadCart = () => {
    try {
      const key = cartStorageKey();
      let raw = localStorage.getItem(key);
      if (!raw) {
        const legacy = localStorage.getItem(`snagcart_${websiteId}`);
        if (legacy) {
          localStorage.setItem(key, legacy);
          raw = legacy;
        }
      }
      const parsed = JSON.parse(raw || '[]');
      setItems(parsed);
      const sub = parsed.reduce((sum, item) => sum + item.price * (item.qty || item.quantity || 0), 0);
      setSubtotal(sub);
      setTotal(sub + (parsed.length > 0 ? 9.99 : 0));
    } catch (e) {
      console.error('Failed to read cart for checkout', e);
    }
  };

  useEffect(() => {
    loadCart();
  }, [websiteId]);

  useEffect(() => {
    if (!websiteId) return;
    fetch(`${paymentsApiBase()}/payments/${websiteId}/gateways`)
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => {
        const list = data.gateways || [];
        setGateways(list);
        setCurrency(data.currency || 'USD');
        if (list.includes('stripe')) setSelectedGateway('stripe');
        else if (list.includes('paypal')) setSelectedGateway('paypal');
        else if (list.length) setSelectedGateway(list[0]);
      })
      .catch(() => setGateways([]));
  }, [API_URL, websiteId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!items.length) {
      toast.error('Your cart is empty');
      return;
    }
    if (!form.email) {
      toast.error('Email is required to checkout');
      return;
    }
    if (!gateways.length) {
      toast.error('Payments are not configured for this site yet');
      return;
    }
    if (!selectedGateway) {
      toast.error('Select a payment method');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        customer_email: form.email,
        customer_name: form.name || 'Guest',
        discount_code: form.discount_code || null,
        gateway: selectedGateway,
        items: items.map((it) => ({
          product_id: it.product_id || it.id,
          name: it.name,
          price: it.price,
          quantity: it.qty || it.quantity || 1,
        })),
      };

      const res = await fetch(`${paymentsApiBase()}/payments/${websiteId}/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Checkout failed');
      }

      if (data.gateway === 'stripe' && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      if (data.gateway === 'paypal' && data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      if (data.gateway === 'razorpay' && data.order_id) {
        await loadRazorpayScript();
        const options = {
          key: data.key_id,
          amount: data.amount,
          currency: data.currency,
          order_id: data.order_id,
          name: 'Store',
          description: `Order ${data.order_number || ''}`,
          prefill: { name: form.name, email: form.email },
          handler: async (response) => {
            const verifyResp = await fetch(`${paymentsApiBase()}/payments/razorpay/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                website_id: websiteId,
                gateway: 'razorpay',
                payment_id: response.razorpay_payment_id,
                order_id: response.razorpay_order_id,
                signature: response.razorpay_signature,
              }),
            });
            const verifyData = await verifyResp.json().catch(() => ({}));
            if (!verifyResp.ok) throw new Error(verifyData.detail || 'Verification failed');
            localStorage.removeItem(cartStorageKey());
            setItems([]);
            toast.success(`Payment successful — order ${data.order_number || ''}`);
            navigate(-1);
          },
        };
        // eslint-disable-next-line no-new
        new window.Razorpay(options).open();
        return;
      }

      throw new Error('Unsupported payment response');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  const currencySymbol = currency === 'INR' ? '₹' : '$';

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-heading font-bold">Store Checkout</h1>
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1.5fr,1fr] gap-6">
          <form onSubmit={handleSubmit} className="space-y-4 bg-zinc-900/60 p-4 rounded-2xl border border-zinc-800">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="mt-1 bg-black/40 border-zinc-700"
                required
              />
            </div>
            <div>
              <Label htmlFor="name">Name (optional)</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="mt-1 bg-black/40 border-zinc-700"
              />
            </div>
            <div>
              <Label htmlFor="discount">Discount code</Label>
              <Input
                id="discount"
                value={form.discount_code}
                onChange={(e) => handleChange('discount_code', e.target.value)}
                placeholder="Enter coupon if you have one"
                className="mt-1 bg-black/40 border-zinc-700"
              />
            </div>
            {gateways.length > 1 && (
              <div>
                <Label>Payment method</Label>
                <div className="flex gap-2 mt-2">
                  {gateways.includes('stripe') && (
                    <Button
                      type="button"
                      variant={selectedGateway === 'stripe' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedGateway('stripe')}
                    >
                      Card (Stripe)
                    </Button>
                  )}
                  {gateways.includes('razorpay') && (
                    <Button
                      type="button"
                      variant={selectedGateway === 'razorpay' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedGateway('razorpay')}
                    >
                      Razorpay
                    </Button>
                  )}
                  {gateways.includes('paypal') && (
                    <Button
                      type="button"
                      variant={selectedGateway === 'paypal' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setSelectedGateway('paypal')}
                    >
                      PayPal
                    </Button>
                  )}
                </div>
              </div>
            )}
            {!gateways.length && (
              <p className="text-xs text-amber-400">
                No payment gateway is configured. Add Stripe or Razorpay in Operate → Integrations.
              </p>
            )}
            <Button type="submit" disabled={loading || !items.length || !gateways.length} className="w-full mt-2">
              {loading ? 'Processing...' : 'Pay securely'}
            </Button>
          </form>

          <div className="space-y-4">
            <div className="bg-zinc-900/60 p-4 rounded-2xl border border-zinc-800">
              <h2 className="font-heading font-semibold mb-3 text-sm">Order Summary</h2>
              {items.length === 0 ? (
                <p className="text-xs text-zinc-500">Your cart is empty.</p>
              ) : (
                <div className="space-y-2 text-sm">
                  {items.map((item) => (
                    <div key={item.id || item.product_id} className="flex justify-between text-xs">
                      <span>
                        {item.name} × {item.qty || item.quantity}
                      </span>
                      <span>
                        {currencySymbol}
                        {(item.price * (item.qty || item.quantity || 0)).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="mt-3 pt-3 border-t border-zinc-800 flex justify-between text-xs">
                    <span>Subtotal</span>
                    <span>
                      {currencySymbol}
                      {subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>Shipping</span>
                    <span>{items.length ? `${currencySymbol}9.99` : `${currencySymbol}0.00`}</span>
                  </div>
                  <div className="flex justify-between text-xs font-semibold">
                    <span>Total</span>
                    <span>
                      {currencySymbol}
                      {total.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreCheckout;
